/**
 * Meta API activity & limits viewer page (`/meta-activity`).
 *
 * Owner/team_lead-only window into every call AdUp made to the Meta (Facebook)
 * Graph API on the agency's behalf, plus how close each ran to the rate limits
 * (business-use-case + app-level usage headers).
 *
 * Two surfaces, stacked:
 *   1. Summary strip — KPI tiles (total calls, throttles, requeues, peak BUC %,
 *      peak app %) + a usage-over-time bar chart driven by the summary
 *      `timeline` (inline SVG, bars coloured by max BUC usage, 80% line marked).
 *   2. Calls table — paginated/filterable feed (shop / operation / status /
 *      date range), status pills via the shared `StatusPill`.
 *
 * Window tabs (7d / 30d) drive the summary endpoint.
 *
 * Shell matches `AuditLog.tsx` / `Stats.tsx`:
 *   PageWrapperMain → subPage → SubPageContentHolder → SubPageInnerContentHolder.
 * NO MUI primitives — plain divs + custom SCSS (`meta-activity/metaActivity.scss`)
 * so the page picks up the legacy theme variables. Charts are inline SVG only —
 * no chart library (mirrors the Stats page).
 *
 * Role-gated to the `meta_activity.view` capability (owner/team_lead). Other
 * roles see an inline "no permission" message inside the legacy chrome.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/meta-activity/metaActivity.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import StatusPill, { StatusTone } from '../../ui/statusPill/StatusPill';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { useCan, useAccessibleShops } from '../../lib/rbac';
import { databaseLargeSVG } from '../../lib/allCustomSVGs';
import {
  MetaActivityEntry,
  MetaActivityMeta,
  MetaActivityFilters,
  MetaActivityStatus,
  MetaActivitySummary,
  MetaActivityTimelineBucket,
  MetaActivityWindow,
  listMetaActivity,
  getMetaActivitySummary,
} from '../../lib/api/meta-activity/meta-activity-api';

const PER_PAGE = 25;

/** The BUC/app-usage threshold above which Meta starts throttling. */
const USAGE_WARN_THRESHOLD = 80;

const STATUS_OPTIONS: MetaActivityStatus[] = [
  'ok',
  'throttled',
  'requeued',
  'failed',
];

const WINDOW_OPTIONS: Array<{ value: MetaActivityWindow; key: string }> = [
  { value: '7d', key: 'MetaActivityWindow7d' },
  { value: '30d', key: 'MetaActivityWindow30d' },
];

// ---------- helpers ----------

/** Format an ISO timestamp into a compact, locale-aware string. */
function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format a millisecond duration in a humane way (<1s shows "Nms"). */
function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remSeconds}s`;
}

/** Format a 0–100 usage percentage. Null → em dash. */
function formatUsagePct(pct: number | null): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return '—';
  return `${Math.round(pct)}%`;
}

/** Humanise an operation token (e.g. `campaign.update` → `campaign · update`). */
function humaniseOperation(operation: string): string {
  if (!operation) return '—';
  return operation
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' · ');
}

/** Map a per-call status to a shared StatusPill tone. */
function statusTone(status: MetaActivityStatus): StatusTone {
  switch (status) {
    case 'ok':
      return 'success';
    case 'throttled':
      return 'warning';
    case 'requeued':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Colour a usage value: green < 50, amber 50–80, red ≥ 80. */
function usageColor(pct: number | null): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return '#cbd5e1';
  if (pct >= USAGE_WARN_THRESHOLD) return '#ef4444'; // red
  if (pct >= 50) return '#f59e0b'; // amber
  return '#22c55e'; // green
}

// ---------- sub-components ----------

const KpiTile: React.FC<{
  label: string;
  value: number | string;
  tone?: 'default' | 'warning' | 'danger';
}> = ({ label, value, tone = 'default' }) => (
  <div className={`metaActivityKpi metaActivityKpi--${tone}`}>
    <div className='metaActivityKpi__value'>{value}</div>
    <div className='metaActivityKpi__label'>{label}</div>
  </div>
);

/**
 * Usage-over-time chart. Inline-SVG/CSS vertical bars (mirrors the Stats page's
 * `statsPlatformChart` pattern — no chart library). Each bar's height maps to
 * the bucket's peak BUC usage (0–100), coloured by that same value. An 80%
 * reference line is drawn across the plot so over-limit buckets read at a
 * glance.
 */
const UsageTimelineChart: React.FC<{
  timeline: MetaActivityTimelineBucket[];
}> = ({ timeline }) => {
  const { t } = useTranslation();

  if (!timeline || timeline.length === 0) {
    return (
      <div className='metaActivityChart__empty'>
        {t('MetaActivityChartEmpty')}
      </div>
    );
  }

  // Bars are scaled against a fixed 0–100 axis (usage is already a percentage),
  // so the 80% line sits at a stable height regardless of the data.
  return (
    <div className='metaActivityChart'>
      <div className='metaActivityChart__plot'>
        {/* 80% threshold reference line (positioned from the top: 100 - 80). */}
        <div
          className='metaActivityChart__threshold'
          style={{ bottom: `${USAGE_WARN_THRESHOLD}%` }}
        >
          <span className='metaActivityChart__thresholdLabel'>
            {t('MetaActivityThresholdLabel', { pct: USAGE_WARN_THRESHOLD })}
          </span>
        </div>
        <div className='metaActivityChart__bars'>
          {timeline.map((b, i) => {
            const usage =
              b.max_buc_usage_pct === null || b.max_buc_usage_pct === undefined
                ? 0
                : Math.max(0, Math.min(100, b.max_buc_usage_pct));
            // Keep a sliver visible for non-zero-call buckets even at 0% usage.
            const height = usage > 0 ? Math.max(2, usage) : b.calls > 0 ? 2 : 0;
            return (
              <div
                key={`${b.bucket_ts}-${i}`}
                className='metaActivityChart__col'
                title={t('MetaActivityChartBarTitle', {
                  ts: formatTimestamp(b.bucket_ts),
                  calls: b.calls,
                  pct: formatUsagePct(b.max_buc_usage_pct),
                })}
              >
                <div
                  className='metaActivityChart__bar'
                  style={{
                    height: `${height}%`,
                    backgroundColor: usageColor(b.max_buc_usage_pct),
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className='metaActivityChart__axis'>
        <span>{formatTimestamp(timeline[0].bucket_ts)}</span>
        {timeline.length > 1 && (
          <span>
            {formatTimestamp(timeline[timeline.length - 1].bucket_ts)}
          </span>
        )}
      </div>
      <div className='metaActivityChart__caption'>
        {t('MetaActivityChartCaption')}
      </div>
    </div>
  );
};

const Pagination: React.FC<{
  meta: MetaActivityMeta;
  onPageChange: (page: number) => void;
  loading: boolean;
}> = ({ meta, onPageChange, loading }) => {
  const { t } = useTranslation();
  const current = meta.current_page || 1;
  const last = Math.max(1, meta.last_page || 1);
  const from = meta.total === 0 ? 0 : (current - 1) * meta.per_page + 1;
  const to = Math.min(meta.total, current * meta.per_page);

  return (
    <div className='metaActivityPagination'>
      <div className='metaActivityPagination__info'>
        {t('MetaActivityPaginationInfo', {
          from,
          to,
          total: meta.total,
        })}
      </div>
      <div className='metaActivityPagination__controls'>
        <button
          type='button'
          className='metaActivityPagination__btn'
          disabled={loading || current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          {t('Previous')}
        </button>
        <span className='metaActivityPagination__page'>
          {t('MetaActivityPageOf', { current, last })}
        </span>
        <button
          type='button'
          className='metaActivityPagination__btn'
          disabled={loading || current >= last}
          onClick={() => onPageChange(current + 1)}
        >
          {t('Next')}
        </button>
      </div>
    </div>
  );
};

// ---------- main ----------

/**
 * Date for the staleness banner: short, absolute, unambiguous. A relative
 * "43 days ago" reads as vague; a date is something you can match against a
 * deploy or an incident.
 */
function formatBannerDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const MetaActivity: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const canView = useCan('meta_activity.view');
  const accessibleShops = useAccessibleShops();

  // Resolve the accessible-shop list (owner sees all → sourced from the
  // identity payload). Used for the shop filter dropdown AND to humanise shop
  // ids/slugs in rows that lack a `shop_name`. Mirrors AuditLog.tsx.
  const identity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );
  const shopOptions = useMemo(() => {
    const fromShops = (identity?.accessible_shops ?? []).map((s) => ({
      slug: s.slug ?? s.brand_slug ?? '',
      name: s.name ?? s.brand_name ?? s.slug ?? s.brand_slug ?? '',
    }));
    const fromBrands = (identity?.accessible_brands ?? []).map((b) => ({
      slug: b.brand_slug ?? '',
      name: b.brand_name ?? b.brand_slug ?? '',
    }));
    const merged = [...fromShops, ...fromBrands].filter((s) => s.slug);
    const seen = new Set<string>();
    return merged.filter((s) => {
      if (seen.has(s.slug)) return false;
      seen.add(s.slug);
      if (accessibleShops === null) return true; // owner: all
      return accessibleShops.includes(s.slug);
    });
  }, [identity, accessibleShops]);

  // ----- window (drives the summary strip + chart) -----
  const [window, setWindow] = useState<MetaActivityWindow>('7d');

  // ----- filter state (drives the calls table) -----
  const [shop, setShop] = useState('');
  const [operation, setOperation] = useState('');
  const [status, setStatus] = useState<MetaActivityStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  // ----- summary state -----
  const [summary, setSummary] = useState<MetaActivitySummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // ----- table state -----
  const [entries, setEntries] = useState<MetaActivityEntry[]>([]);
  const [meta, setMeta] = useState<MetaActivityMeta>({
    total: 0,
    per_page: PER_PAGE,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The summary follows the window tabs and the shop filter (so the strip
  // reflects whichever client is selected), but NOT the table-only filters
  // (operation / status / date range).
  const loadSummary = useCallback(
    async (w: MetaActivityWindow, shopSlug: string) => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const res = await getMetaActivitySummary(w, shopSlug || undefined);
        setSummary(res);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[MetaActivity] summary load failed', err);
        setSummaryError(
          err instanceof Error ? err.message : 'Failed to load summary.'
        );
      } finally {
        setSummaryLoading(false);
      }
    },
    []
  );

  const loadTable = useCallback(
    async (overridePage?: number) => {
      setLoading(true);
      setError(null);
      const filters: MetaActivityFilters = {
        shop: shop || undefined,
        operation: operation || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        page: overridePage ?? page,
        per_page: PER_PAGE,
      };
      try {
        const res = await listMetaActivity(filters);
        setEntries(res.data);
        setMeta(res.meta);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[MetaActivity] table load failed', err);
        const msg =
          err instanceof Error ? err.message : 'Failed to load Meta activity.';
        setError(msg);
        dispatch(setMessage({ message: msg, messageType: 'error' }));
      } finally {
        setLoading(false);
      }
    },
    [shop, operation, status, from, to, page, dispatch]
  );

  // Summary load: on mount + whenever the window or shop changes.
  useEffect(() => {
    if (canView) {
      loadSummary(window, shop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, window, shop]);

  // Table load: on mount + page changes. Filter changes go through
  // `applyFilters` (which resets the page to 1) to avoid double-fetching.
  useEffect(() => {
    if (canView) {
      loadTable(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, page]);

  const applyFilters = () => {
    if (page === 1) {
      loadTable(1);
    } else {
      setPage(1); // triggers the effect above
    }
  };

  const clearFilters = () => {
    setShop('');
    setOperation('');
    setStatus('');
    setFrom('');
    setTo('');
    if (page === 1) {
      setTimeout(() => loadTable(1), 0);
    } else {
      setPage(1);
    }
  };

  const handlePageChange = (next: number) => {
    setPage(next);
  };

  /* ------------------------------------------------------------
     Shell wrapper. When `embedded` (hosted inside the Log Center
     tabbed page) we render only the inner content — the Log Center
     owns the PageWrapperMain/SubPage chrome and the title. Standalone
     (legacy `/meta-activity` route) keeps the full shell.
     ------------------------------------------------------------ */
  const wrapShell = (content: React.ReactNode) =>
    embedded ? (
      <>{content}</>
    ) : (
      <PageWrapperMain>
        <div className='subPage'>
          <SubPageContentHolder>
            <SubPageInnerContentHolder
              title={t('MetaActivity')}
              subTitle={t('MetaActivitySubTitle')}
              backgroundColor={'var(--main-background-color)'}
            >
              {content}
            </SubPageInnerContentHolder>
          </SubPageContentHolder>
        </div>
      </PageWrapperMain>
    );

  /* ============================================================
     No-access state — render the legacy shell so the sidebar stays
     mounted and the user can navigate away.
     ============================================================ */
  if (!canView) {
    return wrapShell(
      <div className='metaActivityPage'>
        <div className='metaActivityPage__noAccess'>
          {t('MetaActivityNoAccess')}
        </div>
      </div>
    );
  }

  /* ============================================================
     Main view
     ============================================================ */
  return wrapShell(
    <div className='metaActivityPage'>
      {/* ---------- Window tabs ---------- */}
              <div className='metaActivityWindow' role='tablist'>
                {WINDOW_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type='button'
                    role='tab'
                    aria-selected={window === opt.value}
                    className={`metaActivityWindow__tab ${
                      window === opt.value
                        ? 'metaActivityWindow__tab--active'
                        : ''
                    }`}
                    onClick={() => setWindow(opt.value)}
                  >
                    {t(opt.key)}
                  </button>
                ))}
              </div>

              {/* ---------- Pipeline health ----------
                   An empty table cannot tell you WHY it is empty. When the
                   gateway is logging Facebook tool calls but no audit rows are
                   arriving behind them, say so — that state went unnoticed for
                   six weeks (2026-07-13 → 08-25) precisely because "broken" and
                   "idle" render identically. */}
              {summary?.pipeline?.stale && (
                <div className='metaActivityPage__staleBanner' role='status'>
                  <span>
                    {summary.pipeline.newest_call_at
                      ? t('MetaActivityPipelineStaleSince', {
                          date: formatBannerDate(
                            summary.pipeline.newest_call_at
                          ),
                          defaultValue:
                            'Meta calls are being made, but none have been recorded here since {{date}} — the audit pipeline may be down.',
                        })
                      : t('MetaActivityPipelineStaleNever', {
                          defaultValue:
                            'Meta calls are being made, but none have ever been recorded here — the audit pipeline may be down.',
                        })}
                  </span>
                </div>
              )}

              {/* ---------- Summary strip ---------- */}
              <div className='metaActivitySummary'>
                {summaryError && (
                  <div className='metaActivityPage__error' role='alert'>
                    <span>{summaryError}</span>
                    <button
                      type='button'
                      className='metaActivityPage__retryBtn'
                      onClick={() => loadSummary(window, shop)}
                    >
                      {t('Retry')}
                    </button>
                  </div>
                )}

                {summaryLoading && !summary ? (
                  <div className='metaActivityPage__loading'>
                    <LoadingSpinner
                      customLoadingMsg={t('LoadingMetaActivitySummary')}
                    />
                  </div>
                ) : (
                  summary && (
                    <>
                      <div className='metaActivityKpiGrid'>
                        <KpiTile
                          label={t('MetaActivityKpiTotalCalls')}
                          value={summary.total_calls}
                        />
                        <KpiTile
                          label={t('MetaActivityKpiThrottles')}
                          value={summary.throttle_events}
                          tone={
                            summary.throttle_events > 0 ? 'warning' : 'default'
                          }
                        />
                        <KpiTile
                          label={t('MetaActivityKpiRequeues')}
                          value={summary.requeue_events}
                          tone={
                            summary.requeue_events > 0 ? 'warning' : 'default'
                          }
                        />
                        <KpiTile
                          label={t('MetaActivityKpiPeakBuc')}
                          value={formatUsagePct(summary.peak_buc_usage_pct)}
                          tone={
                            (summary.peak_buc_usage_pct ?? 0) >=
                            USAGE_WARN_THRESHOLD
                              ? 'danger'
                              : 'default'
                          }
                        />
                        <KpiTile
                          label={t('MetaActivityKpiPeakApp')}
                          value={formatUsagePct(summary.peak_app_usage_pct)}
                          tone={
                            (summary.peak_app_usage_pct ?? 0) >=
                            USAGE_WARN_THRESHOLD
                              ? 'danger'
                              : 'default'
                          }
                        />
                      </div>

                      {/* ---------- Usage-over-time chart ---------- */}
                      <div className='metaActivitySection'>
                        <div className='metaActivitySection__title'>
                          {t('MetaActivityChartTitle')}
                        </div>
                        <div className='metaActivitySection__divider' />
                        <UsageTimelineChart timeline={summary.timeline} />
                      </div>
                    </>
                  )
                )}
              </div>

              {/* ---------- Filter bar ---------- */}
              <div className='metaActivityFilters'>
                <div className='metaActivityFilters__row'>
                  <div className='metaActivityFilters__field'>
                    <label className='metaActivityFilters__label'>
                      {t('MetaActivityFilterShop')}
                    </label>
                    <select
                      className='metaActivityFilters__select'
                      value={shop}
                      onChange={(e) => setShop(e.target.value)}
                    >
                      <option value=''>{t('MetaActivityAllShops')}</option>
                      {shopOptions.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='metaActivityFilters__field'>
                    <label className='metaActivityFilters__label'>
                      {t('MetaActivityFilterOperation')}
                    </label>
                    <input
                      className='metaActivityFilters__input'
                      type='text'
                      value={operation}
                      placeholder={t('MetaActivityOperationPlaceholder')}
                      onChange={(e) => setOperation(e.target.value)}
                    />
                  </div>

                  <div className='metaActivityFilters__field'>
                    <label className='metaActivityFilters__label'>
                      {t('MetaActivityFilterStatus')}
                    </label>
                    <select
                      className='metaActivityFilters__select'
                      value={status}
                      onChange={(e) =>
                        setStatus(e.target.value as MetaActivityStatus | '')
                      }
                    >
                      <option value=''>{t('MetaActivityAllStatuses')}</option>
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {t(`MetaActivityStatus_${s}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='metaActivityFilters__field'>
                    <label className='metaActivityFilters__label'>
                      {t('MetaActivityFilterFrom')}
                    </label>
                    <input
                      className='metaActivityFilters__input'
                      type='date'
                      value={from}
                      max={to || undefined}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>

                  <div className='metaActivityFilters__field'>
                    <label className='metaActivityFilters__label'>
                      {t('MetaActivityFilterTo')}
                    </label>
                    <input
                      className='metaActivityFilters__input'
                      type='date'
                      value={to}
                      min={from || undefined}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className='metaActivityFilters__actions'>
                  <MainSquareButton
                    value={t('MetaActivityApplyFilters')}
                    type='button'
                    normal
                    onClick={applyFilters}
                  />
                  <button
                    type='button'
                    className='metaActivityFilters__clearBtn'
                    onClick={clearFilters}
                  >
                    {t('MetaActivityClearFilters')}
                  </button>
                </div>
              </div>

              {error && (
                <div className='metaActivityPage__error' role='alert'>
                  <span>{error}</span>
                  <button
                    type='button'
                    className='metaActivityPage__retryBtn'
                    onClick={() => loadTable(page)}
                  >
                    {t('Retry')}
                  </button>
                </div>
              )}

              {/* ---------- Calls table ---------- */}
              {loading && entries.length === 0 ? (
                <div className='metaActivityPage__loading'>
                  <LoadingSpinner
                    customLoadingMsg={t('LoadingMetaActivity')}
                  />
                </div>
              ) : (
                <div className='metaActivityTable'>
                  <div className='metaActivityTable__head'>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColTime')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColClient')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColEmployee')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColOperation')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColAccount')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColMethod')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColStatus')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColDuration')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColBuc')}
                    </div>
                    <div className='metaActivityTable__cell'>
                      {t('MetaActivityColApp')}
                    </div>
                  </div>

                  {entries.length === 0 ? (
                    <div className='metaActivityTable__empty'>
                      <TeachingEmptyState
                        icon={databaseLargeSVG}
                        title={t('MetaActivityEmptyTitle')}
                        body={t('MetaActivityEmptyBody')}
                        size='compact'
                      />
                    </div>
                  ) : (
                    entries.map((entry) => {
                      const shopLabel =
                        entry.shop_name ||
                        shopOptions.find(
                          (s) => s.slug === entry.shop_slug
                        )?.name ||
                        entry.shop_slug ||
                        (entry.shop_id != null
                          ? String(entry.shop_id)
                          : '—');
                      const bucWarn =
                        (entry.buc_usage_pct ?? 0) >= USAGE_WARN_THRESHOLD;
                      const appWarn =
                        (entry.app_usage_pct ?? 0) >= USAGE_WARN_THRESHOLD;
                      return (
                        <div className='metaActivityTable__row' key={entry.id}>
                          <div
                            className='metaActivityTable__cell metaActivityTable__time'
                            data-label={t('MetaActivityColTime')}
                          >
                            {formatTimestamp(entry.created_at)}
                          </div>
                          <div
                            className='metaActivityTable__cell metaActivityTable__client'
                            data-label={t('MetaActivityColClient')}
                            title={shopLabel}
                          >
                            {shopLabel}
                          </div>
                          <div
                            className='metaActivityTable__cell metaActivityTable__employee'
                            data-label={t('MetaActivityColEmployee')}
                            title={entry.employee_email || entry.employee_id || ''}
                          >
                            {entry.employee_email || entry.employee_id || '—'}
                          </div>
                          <div
                            className='metaActivityTable__cell metaActivityTable__operation'
                            data-label={t('MetaActivityColOperation')}
                            title={entry.operation || ''}
                          >
                            <span>{humaniseOperation(entry.operation)}</span>
                            <span className='metaActivityTable__source'>
                              {t(`MetaActivitySource_${entry.source}`, {
                                defaultValue: entry.source,
                              })}
                            </span>
                          </div>
                          <div
                            className='metaActivityTable__cell metaActivityTable__account'
                            data-label={t('MetaActivityColAccount')}
                            title={entry.ad_account_id || ''}
                          >
                            {entry.ad_account_id || '—'}
                          </div>
                          <div
                            className='metaActivityTable__cell'
                            data-label={t('MetaActivityColMethod')}
                          >
                            {entry.http_method ? (
                              <span className='metaActivityMethod'>
                                {entry.http_method.toUpperCase()}
                              </span>
                            ) : (
                              '—'
                            )}
                          </div>
                          <div
                            className='metaActivityTable__cell'
                            data-label={t('MetaActivityColStatus')}
                          >
                            <StatusPill
                              tone={statusTone(entry.status)}
                              withDot
                              title={entry.error || undefined}
                            >
                              {t(`MetaActivityStatus_${entry.status}`, {
                                defaultValue: entry.status,
                              })}
                            </StatusPill>
                          </div>
                          <div
                            className='metaActivityTable__cell metaActivityTable__duration'
                            data-label={t('MetaActivityColDuration')}
                          >
                            {formatDuration(entry.duration_ms)}
                          </div>
                          <div
                            className={`metaActivityTable__cell metaActivityTable__usage ${
                              bucWarn ? 'metaActivityTable__usage--warn' : ''
                            }`}
                            data-label={t('MetaActivityColBuc')}
                          >
                            {formatUsagePct(entry.buc_usage_pct)}
                          </div>
                          <div
                            className={`metaActivityTable__cell metaActivityTable__usage ${
                              appWarn ? 'metaActivityTable__usage--warn' : ''
                            }`}
                            data-label={t('MetaActivityColApp')}
                          >
                            {formatUsagePct(entry.app_usage_pct)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

      {/* ---------- Pagination ---------- */}
      {entries.length > 0 && (
        <Pagination
          meta={meta}
          onPageChange={handlePageChange}
          loading={loading}
        />
      )}
    </div>
  );
};

export default MetaActivity;
